import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

const DTO_LANGUAGE_MAP: Record<string, string> = {
  de: 'deutsch',
  en: 'english',
};

const LANGUAGE_STORAGE_KEY = 'locale';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translocoService = inject(TranslocoService);

  public readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  public readonly activeLangLabel = computed(() =>
    this.activeLang().toUpperCase(),
  );

  constructor() {
    this.restoreLanguage();
  }

  private restoreLanguage(): void {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (!stored) return;
    const available = this.getAvailableLanguageIds();
    if (available.includes(stored)) {
      this.translocoService.setActiveLang(stored);
    }
  }

  public setActiveLang(languageId: string): void {
    this.translocoService.setActiveLang(languageId);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, languageId);
  }

  public getAvailableLanguageIds(): string[] {
    return this.translocoService.getAvailableLangs().map((language) => {
      if (typeof language === 'string') {
        return language;
      }
      return language.id;
    });
  }

  public getDisplayLabel(languageId: string): string {
    const translationKey = `consultation.language.${languageId}`;
    const translatedLabel = this.translocoService.translate(translationKey);

    if (translatedLabel && translatedLabel !== translationKey) {
      return translatedLabel;
    }

    return languageId.toUpperCase();
  }

  public toDtoLanguageKey(languageId: string): string {
    const normalized = languageId.toLowerCase();
    return DTO_LANGUAGE_MAP[normalized] ?? normalized;
  }

  public getActiveDtoLanguageKey(): string {
    return this.toDtoLanguageKey(this.translocoService.getActiveLang());
  }
}
